import React from 'react';
import PropTypes from 'prop-types';
import Platform from "react-native-web/dist/exports/Platform";
import StyleSheet from "react-native-web/dist/exports/StyleSheet";
import View from "react-native-web/dist/exports/View";
import { validateThemeTokens, resolveThemeTokens } from '../ThemeProvider';
import { a11yProps, getTokensSetPropType, selectSystemProps, selectTokens, viewProps } from '../utils';
import ScrollViewEnd from './ScrollViewEnd';
import { getItemPositionScrollTarget, itemPositionsPropType } from './itemPositions';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const [selectProps, selectedSystemPropTypes] = selectSystemProps([a11yProps, viewProps]);
const tokenKeys = ['nextIcon', 'previousIcon', 'gutter', 'borderBottomWidth', 'borderBottomColor', 'buttonClearance'];
export const selectHorizontalScrollTokens = (themeTokens, prefix) => selectTokens(tokenKeys, themeTokens, prefix);
const selectBorderStyles = (borderBottomWidth, borderBottomColor) => ({
  borderBottomWidth,
  borderBottomColor
});

/**
 * Base component that scrolls horizontally and displays left and right buttons if
 * there is content to the left and the right which can be scrolled to.
 *
 * @TODO revisit `ScrollButton` after IconButton is stable.
 */
const HorizontalScroll = /*#__PURE__*/React.forwardRef((_ref, ref) => {
  let {
    ScrollButton,
    tokens,
    itemPositions,
    children,
    ...rest
  } = _ref;
  const {
    nextIcon,
    previousIcon,
    gutter,
    borderBottomWidth,
    borderBottomColor,
    buttonClearance = 0
  } = validateThemeTokens(resolveThemeTokens(tokens),
  // Allow missing icons
  getTokensSetPropType(tokenKeys, {
    partial: true
  }), 'HorizontalScroll');
  const [containerWidth, setContainerWidth] = React.useState(0);
  const [contentWidth, setContentWidth] = React.useState(0);
  const handleContentWidth = width => setContentWidth(width);
  const handleContainerLayout = _ref2 => {
    let {
      nativeEvent: {
        layout: {
          width
        }
      }
    } = _ref2;
    return setContainerWidth(width);
  };

  // Update the scroll position only when scrolling actions ends to minimally rerender
  // and update which buttons are conditionally rendered.
  const [scrollOffset, setScrollOffset] = React.useState(0);
  const handleScrollEnd = _ref3 => {
    let {
      nativeEvent: {
        contentOffset: {
          x
        }
      }
    } = _ref3;
    setScrollOffset(x);
  };
  const scrollMax = Math.max(0, contentWidth - containerWidth);
  const showNextButton = scrollOffset < scrollMax;
  const showPreviousButton = scrollOffset > 0;
  const scrollRef = React.useRef(null);
  const scrollTo = targetX => {
    if (typeof scrollRef.current?.scrollTo === 'function') {
      const x = getItemPositionScrollTarget(targetX, scrollMax, itemPositions.positions, buttonClearance);
      scrollRef.current.scrollTo({
        x,
        animated: true
      });
      if (Platform.OS === 'android') {
        // React Native calls onScrollMomentumEnd after scrollTo for iOS, but not Android.
        // See https://github.com/facebook/react-native/issues/26661
        // There's also no callback for when scrollTo finishes.
        setTimeout(() => setScrollOffset(x), 300);
      }
    }
  };
  // Make scrollTo and relevant co-ordinates available to any component with an interest in itemPositions.
  // ItemPositions is a mutable ref object intended to have its properties mutated without any rerenders.
  /* eslint-disable-next-line no-param-reassign */
  itemPositions.scrollTo = scrollTo;
  /* eslint-disable-next-line no-param-reassign */
  itemPositions.containerWidth = containerWidth;
  /* eslint-disable-next-line no-param-reassign */
  itemPositions.contentWidth = contentWidth;
  /* eslint-disable-next-line no-param-reassign */
  itemPositions.scrollOffset = scrollOffset;
  const hasWidths = itemPositions.contentWidth > 0;
  React.useEffect(() => {
    if (hasWidths) itemPositions.setIsReady(true);
    // itemPositions is a ref object so this should occur only when elements' widths are set in state
  }, [hasWidths, itemPositions]);

  // When scrolling, allow clearance around scroll buttons, so e.g. this...
  // (<-) Item 5   Item 6   Item 7   Item 8   It(->)
  // ...doesn't scroll like this with Item 9 never being properly visible:
  // (<-)em 9   Item 10  Item 11  Item 12  Item (->)
  const scrollDistance = containerWidth - buttonClearance * 2;
  const scrollNext = () => scrollTo(scrollOffset + scrollDistance);
  const scrollPrevious = () => scrollTo(scrollOffset - scrollDistance);

  // On Web, being scrollable in one direction prevents overflow: visible in the other direction.
  // Apply a fixed margin gutter that tab highlight effects can overhang without getting trimmed.
  return /*#__PURE__*/_jsxs(View, {
    ref: ref,
    style: staticStyles.container,
    children: [/*#__PURE__*/_jsx(View, {
      style: selectBorderStyles(borderBottomWidth, borderBottomColor),
      children: /*#__PURE__*/_jsx(ScrollViewEnd, {
        onScrollEnd: handleScrollEnd,
        ref: scrollRef,
        horizontal: true,
        onContentSizeChange: handleContentWidth,
        onLayout: handleContainerLayout,
        showsHorizontalScrollIndicator: false,
        contentContainerStyle: staticStyles.scrollContainer,
        ...selectProps(rest),
        children: children
      })
    }), showPreviousButton && /*#__PURE__*/_jsx(ScrollButton, {
      icon: previousIcon,
      onPress: scrollPrevious,
      offset: gutter,
      direction: "previous"
    }), showNextButton && /*#__PURE__*/_jsx(ScrollButton, {
      icon: nextIcon,
      onPress: scrollNext,
      offset: gutter,
      direction: "next"
    })]
  });
});
HorizontalScroll.displayName = 'HorizontalScroll';
const staticStyles = StyleSheet.create({
  scrollContainer: {
    // Gives inner container width from content, allowing it to exceed its container's width
    alignSelf: 'flex-start',
    flexGrow: 1
  },
  container: {
    // Vertically center the ScrollButtons buttons
    justifyContent: 'center'
  }
});
HorizontalScroll.propTypes = {
  ...selectedSystemPropTypes,
  itemPositions: itemPositionsPropType,
  ScrollButton: PropTypes.elementType,
  tokens: getTokensSetPropType(tokenKeys, {
    allowFunction: true
  }),
  children: PropTypes.node
};
export default HorizontalScroll;